import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useViewModeStore } from '../stores/viewModeStore';

const CRM_API_URL =
  (import.meta as any).env.VITE_CRM_API_URL || `http://${window.location.hostname}:8000`;

async function fetchManagerViewPerms(token?: string | null): Promise<Record<string, boolean>> {
  try {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${CRM_API_URL}/api/permissions/me`, {
      headers,
      credentials: 'include',
    });
    if (!res.ok) {
      console.warn(`[useManagerViewPermission] Permissions fetch failed: ${res.status} from ${CRM_API_URL}`);
      return {};
    }
    const data = await res.json();

    const granularPerms: Record<string, any> = {};
    data.permissions?.forEach((p: any) => {
      granularPerms[p.feature] = p.permissions;
    });
    try {
      localStorage.setItem('modular_permissions', JSON.stringify(granularPerms));
    } catch {
      // Ignore quota errors.
    }

    const managerview = data.permissions?.find((p: any) => p.feature === 'managerview');
    return (managerview?.permissions as Record<string, boolean>) ?? {};
  } catch (err) {
    console.warn(`[useManagerViewPermission] Network error fetching from ${CRM_API_URL}:`, err);
    return {};
  }
}

export function useManagerViewPermission() {
  const { isAdmin, isManager, token } = useAuth();
  const { viewMode } = useViewModeStore();

  const { data: perms = {}, isFetched } = useQuery<Record<string, boolean>>({
    queryKey: ['managerViewPerms', token],
    queryFn: () => fetchManagerViewPerms(token),
    enabled: isAdmin || isManager || !!token,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const managerViewEnabled =
    isAdmin ||
    perms['enableManagerView'] === true ||
    (isManager && isFetched && !('enableManagerView' in perms)) ||
    (isManager && !isFetched);

 
  const isManagerActive = (isAdmin || isManager) && managerViewEnabled && viewMode === 'manager';
  const isUserView = !isManagerActive;

  const hasPerm = (key: string): boolean => {
    if (isAdmin) return true;
    if (isUserView) return false;
    return perms[key] !== false;
  };

  return {
    isUserView,
    isManagerActive,
    managerViewEnabled,

    canViewPayroll: hasPerm('hrShowPayroll'),
    canViewTeamApprovals: hasPerm('hrShowTeamApprovals'),

    canViewAllEmployees: hasPerm('hrShowAllEmployees'),
    canSearchEmployees: hasPerm('hrSearchEmployees'),
    canFilterByDepartment: hasPerm('hrFilterByDepartment'),
    canFilterByStatus: hasPerm('hrFilterByStatus'),
    canAddEmployee: hasPerm('hrAddEmployee'),
    canViewLeaveBalance: hasPerm('hrShowLeaveBalance'),

    canEditEmployee: hasPerm('hrEditEmployee'),
    canViewCompensation: hasPerm('hrShowCompensation'),
    canViewSalaryDetails: hasPerm('hrShowSalaryDetails'),

    canViewEmployeeLeaves: hasPerm('hrShowEmployeeLeaves'),
    canViewLeaveCalendarAll: hasPerm('hrShowLeaveCalendarAll'),

    canViewTeamPerformance: hasPerm('hrShowTeamPerformance'),
    canViewTeamReviews: hasPerm('hrShowTeamReviews'),
    canViewManagerFeedback: hasPerm('hrShowManagerFeedback'),
    canSelectReviewEmployee: hasPerm('hrSelectReviewEmployee'),

    canDoOnboardingActions: hasPerm('hrShowOnboardingActions'),
    canEditDeleteOnboarding: hasPerm('hrEditDeleteOnboarding'),
    canViewOffboardingDetails: hasPerm('hrShowOffboardingDetails'),
    canViewAllBuddyAssignments: hasPerm('hrShowAllBuddyAssignments'),
  };
}
